// src/pages/user/EngineeringPlanPage.jsx
import { useEffect, useMemo, useState } from "react";
import { FileText, RefreshCw, Search, Filter, Eye, Download, X } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import toast from "react-hot-toast";
import { injectUserStyles } from "./userStyles";

const STYLE_ID = "eng-plan-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .ep-table-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; }
    .ep-table { width:100%; border-collapse:collapse; font-size:13px; }
    .ep-table thead tr { background:#f9fafb; border-bottom:1px solid #e8ecf0; }
    .ep-table th {
      padding:10px 16px; text-align:left;
      font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em;
      color:#9ca3af; white-space:nowrap;
    }
    .ep-table td { padding:13px 16px; vertical-align:top; border-bottom:1px solid #f3f4f6; }
    .ep-table tbody tr:last-child td { border-bottom:none; }
    .ep-table tbody tr:hover td { background:#fafafa; }
    .ep-project-name { font-size:13.5px; font-weight:500; color:#0c1118; }
    .ep-request-id   { font-family:'DM Sans',sans-serif; font-size:10px; color:#d1d5db; margin-top:2px; }
    .ep-badge {
      display:inline-flex; align-items:center;
      padding:3px 9px; border-radius:5px; font-family:'DM Sans',sans-serif;
      font-size:10px; letter-spacing:.06em; font-weight:500; border:1px solid;
    }
    .ep-badge-processing { background:rgba(249,171,20,.1); color:#92400e; border-color:rgba(249,171,20,.2); }
    .ep-badge-completed  { background:rgba(16,185,129,.1); color:#065f46; border-color:rgba(16,185,129,.2); }
    .ep-badge-pending    { background:rgba(59,130,246,.1); color:#1e40af; border-color:rgba(59,130,246,.2); }
    .ep-badge-canceled   { background:#f3f4f6; color:#6b7280; border-color:#e2e8f0; }
    .ep-search-wrap { position:relative; flex:1; max-width:360px; }
    .ep-search-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#9ca3af; }
    .ep-search-input {
      width:100%; padding:8px 12px 8px 34px;
      border:1px solid #e2e8f0; border-radius:7px; font-size:13px;
      font-family:'DM Sans',sans-serif; outline:none; transition:border-color 150ms;
      box-sizing:border-box;
    }
    .ep-search-input:focus { border-color:#f9ab14; }
    .ep-filter-select {
      padding:8px 12px 8px 34px; border:1px solid #e2e8f0; border-radius:7px;
      font-size:13px; font-family:'DM Sans',sans-serif; background:#fff; outline:none;
      transition:border-color 150ms;
    }
    .ep-filter-select:focus { border-color:#f9ab14; }
    .ep-filter-wrap { position:relative; }
    .ep-filter-icon { position:absolute; left:10px; top:50%; transform:translateY(-50%); color:#9ca3af; }
    .ep-viewer-backdrop {
      position:fixed; inset:0; z-index:50;
      background:rgba(0,0,0,.65); backdrop-filter:blur(4px);
      display:flex; align-items:center; justify-content:center; padding:24px;
    }
    .ep-viewer-modal {
      background:#0c1118; border-radius:12px; overflow:hidden;
      width:100%; max-width:900px; height:85vh;
      display:flex; flex-direction:column;
    }
    .ep-viewer-head {
      display:flex; align-items:center; justify-content:space-between;
      padding:14px 20px; border-bottom:1px solid rgba(255,255,255,.1);
    }
    .ep-viewer-title { font-size:14px; font-weight:600; color:#fff; }
    .ep-viewer-sub   { font-family:'DM Sans',sans-serif; font-size:10px; color:rgba(255,255,255,.4); margin-top:2px; letter-spacing:.06em; }
    .ep-viewer-close {
      width:32px; height:32px; border-radius:50%;
      background:rgba(255,255,255,.07); border:none; color:rgba(255,255,255,.6);
      cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 150ms;
    }
    .ep-viewer-close:hover { background:rgba(255,255,255,.15); color:#fff; }
    .ep-act-btn {
      display:inline-flex; align-items:center; gap:5px;
      padding:5px 10px; border-radius:6px; font-size:11.5px; font-weight:500;
      cursor:pointer; border:1px solid; transition:background 150ms; white-space:nowrap;
    }
    .ep-act-view   { background:#fff; color:#0c1118; border-color:#e2e8f0; }
    .ep-act-view:hover  { background:#f3f4f6; }
    .ep-act-view:disabled { opacity:.4; cursor:default; }
    .ep-act-rework { background:rgba(249,171,20,.1); color:#92400e; border-color:rgba(249,171,20,.25); }
    .ep-act-rework:hover { background:rgba(249,171,20,.18); }
    .ep-approval-select {
      padding:4px 8px; border:1px solid #e2e8f0; border-radius:5px;
      font-size:11px; font-family:'DM Sans',sans-serif; background:#fff; outline:none;
    }
    .ep-approval-select:focus { border-color:#f9ab14; }
  `;
  document.head.appendChild(el);
}

/* ── Helpers ── */
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function formatDate(date) {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function formatMoney(amountCents, currency) {
  if (!amountCents && amountCents !== 0) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency || "USD" }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency || "USD"}`;
  }
}

function badgeClass(status) {
  switch (status) {
    case "PROCESSING":      return "ep-badge ep-badge-processing";
    case "COMPLETED":       return "ep-badge ep-badge-completed";
    case "PENDING_PAYMENT": return "ep-badge ep-badge-pending";
    case "CANCELED": case "CANCELLED": return "ep-badge ep-badge-canceled";
    default: return "ep-badge ep-badge-canceled";
  }
}

function statusLabel(status) {
  switch (status) {
    case "PROCESSING":      return "Under Review";
    case "COMPLETED":       return "Ready";
    case "PENDING_PAYMENT": return "Awaiting Payment";
    case "CANCELED": case "CANCELLED": return "Cancelled";
    default: return status || "—";
  }
}

function typeLabel(row) {
  if (!row.type || row.type === "NEW") return "New Plan";
  if (row.type === "REWORK") { const n = row.reworkRound ?? 0; return n > 0 ? `Rework #${n}` : "Rework"; }
  return row.type;
}

/* ── Main ── */
export default function EngineeringPlansPage() {
  const { user } = useAuth();
  const [loading, setLoading]               = useState(true);
  const [rows, setRows]                     = useState([]);
  const [q, setQ]                           = useState("");
  const [statusFilter, setStatusFilter]     = useState("all");
  const [selected, setSelected]             = useState(null);
  const [updatingApprovalId, setUpdatingApprovalId] = useState(null);

  useEffect(() => { injectLocal(); }, []);

  const primaryRole        = (user?.primaryOrgRole || "").toLowerCase();
  const isCompany          = user?.accountType === "organization" && primaryRole !== "";
  const isCompanyManager   = isCompany && ["owner", "admin", "staff"].includes(primaryRole);

  async function refresh() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/purchase-plans/my`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load plans");
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  const filteredRows = useMemo(() => {
    let list = rows;
    const term = q.trim().toLowerCase();
    if (term) list = list.filter((r) => (r.projectName || "").toLowerCase().includes(term) || (r.id || "").toLowerCase().includes(term));
    if (statusFilter !== "all") list = list.filter((r) => r.status === statusFilter);
    return list;
  }, [rows, q, statusFilter]);

  async function updateCompanyApproval(rowId, nextStatus) {
    setUpdatingApprovalId(rowId);
    try {
      const res = await fetch(`${API_BASE}/api/purchase-plans/${rowId}/company-approval`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ companyApprovalStatus: nextStatus }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || "Failed"); }
      toast.success("Member access updated");
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to update member access");
    } finally {
      setUpdatingApprovalId(null);
    }
  }

  async function requestRework(row) {
    const confirmed = window.confirm(`Request a rework for "${row.projectName}"?\n\nWe'll create a new rework request for this project so the team can update your engineered plan.`);
    if (!confirmed) return;
    try {
      const res = await fetch(`${API_BASE}/api/purchase-plans/${row.id}/rework`, { method: "POST", credentials: "include" });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || "Failed"); }
      const data = await res.json();
      if (data.state === "rework_created")  toast.success("Rework request submitted. We'll notify you when the updated plan is ready.");
      else if (data.state === "has_pending") toast("A plan request for this project is already in progress.");
      else if (data.state === "not_completed") toast("You can only request a rework after the first plan is completed.");
      else toast.success("Rework request processed.");
      await refresh();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Failed to request rework");
    }
  }

  const KPIS = [
    { accent: "#0c1118", val: rows.length,                                              lbl: "Total Requests" },
    { accent: "#f9ab14", val: rows.filter((r) => r.status === "PROCESSING").length,     lbl: "Under Review"   },
    { accent: "#10b981", val: rows.filter((r) => r.status === "COMPLETED").length,      lbl: "Completed"      },
    { accent: "#3b82f6", val: rows.filter((r) => r.status === "PENDING_PAYMENT").length, lbl: "Awaiting Payment" },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="up-title">Engineering Plans</div>
          <div className="up-sub">Your PE-stamped plan requests and downloads</div>
        </div>
        <button className="up-btn up-btn-outline" onClick={refresh} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="up-kpi-grid">
        {KPIS.map((k) => (
          <div className="up-kpi" key={k.lbl}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val">{loading ? "—" : k.val}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "20px" }}>
        <div className="ep-search-wrap">
          <Search size={14} className="ep-search-icon" />
          <input
            className="ep-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by project name or request ID…"
          />
        </div>
        <div className="ep-filter-wrap">
          <Filter size={14} className="ep-filter-icon" />
          <select className="ep-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All statuses</option>
            <option value="PROCESSING">Under review</option>
            <option value="COMPLETED">Ready</option>
            <option value="PENDING_PAYMENT">Awaiting payment</option>
            <option value="CANCELED">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="ep-table-card">
        {loading ? (
          <div className="up-loading"><div className="up-spinner" /> Loading engineering plans…</div>
        ) : filteredRows.length === 0 ? (
          <div className="up-empty">
            <FileText size={36} style={{ color: "#d1d5db", margin: "0 auto" }} />
            <div className="up-empty-title">No engineering plan requests found</div>
            <div className="up-empty-sub">Request a plan from the 3D editor on any project</div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="ep-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Completed</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const canView        = Boolean(row.fileUrl);
                  const isOrgRequest   = Boolean(row.orgId);
                  const canManageMemberAccess = isCompanyManager && isOrgRequest;
                  const approval       = row.companyApprovalStatus || "pending";
                  return (
                    <tr key={row.id}>
                      <td>
                        <div className="ep-project-name">{row.projectName}</div>
                        <div className="ep-request-id">ID: {row.id.slice(0, 8)}…</div>
                      </td>
                      <td style={{ fontSize: "13px", color: "#374151" }}>{typeLabel(row)}</td>
                      <td><span className={badgeClass(row.status)}>{statusLabel(row.status)}</span></td>
                      <td style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: "#6b7280" }}>{formatDate(row.createdAt)}</td>
                      <td style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "11px", color: "#6b7280" }}>{formatDate(row.processedAt)}</td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px", flexWrap: "wrap" }}>
                          <button className="ep-act-btn ep-act-view" disabled={!canView} onClick={() => canView && setSelected(row)}>
                            <Eye size={12} /> View
                          </button>
                          {canView && row.status === "COMPLETED" && (
                            <button className="ep-act-btn ep-act-rework" onClick={() => requestRework(row)}>
                              <RefreshCw size={12} /> Rework
                            </button>
                          )}
                          {canManageMemberAccess && (
                            <select
                              className="ep-approval-select"
                              value={approval}
                              disabled={updatingApprovalId === row.id}
                              onChange={(e) => updateCompanyApproval(row.id, e.target.value)}
                            >
                              <option value="pending">Pending</option>
                              <option value="approved">Approved</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          )}
                          {!canView && (
                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "10px", color: "#d1d5db" }}>PDF not ready</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PDF Viewer */}
      {selected?.fileUrl && (
        <div className="ep-viewer-backdrop" onClick={() => setSelected(null)}>
          <div className="ep-viewer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ep-viewer-head">
              <div>
                <div className="ep-viewer-title">{selected.projectName}</div>
                <div className="ep-viewer-sub">{typeLabel(selected)} · {statusLabel(selected.status)} · ID: {selected.id.slice(0, 8)}…</div>
              </div>
              <button className="ep-viewer-close" onClick={() => setSelected(null)}>
                <X size={14} />
              </button>
            </div>
            <div style={{ flex: 1, background: "#1a1f2e" }}>
              <iframe src={selected.fileUrl} title="Engineering Plan PDF" style={{ width: "100%", height: "100%", border: "none" }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
